import { AfterViewChecked, Component, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { DisplayGridColumnSettings } from 'src/app/models/gridColumnSettings.model';
import { GridViewService } from 'src/app/services/grid-view.service';
import { GridView } from 'src/app/models/gridView.model';
import { ToastService } from 'src/app/services/toast.service';
import { Lookup } from 'src/app/models/lookup.model';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { MatChipEditedEvent, MatChipInputEvent } from '@angular/material/chips';
import { MatMenuTrigger } from '@angular/material/menu';
import { ColumnFilter, FilterOperator, QuickFilter, QuickFilterToggle } from 'src/app/models/columnFilter.model';
import { GridSettingsExtraTab } from 'src/app/models/gridSettingsExtraTab.model';
import { GridSortSettings } from 'src/app/models/gridSortSettings.model';
import { HttpErrorResponse } from '@angular/common/http';
import { GridSettingsService } from './grid-settings.service';
import { OrganizationLevel } from 'src/app/models/organizationLevel.model';
import { OrganizationLevelShareService } from '../../../services/organization-level-share.service';
import { SearchInputComponent } from './search-input/search-input.component';
import { trackById, trackByIndex } from 'src/app/shared/utils/track-by.util';
import { saveGridView } from 'src/app/shared/grid/grid-view-default.helper';

@Component({
  standalone: false,
  selector: 'app-grid-settings-flyout',
  templateUrl: './grid-settings-flyout.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./grid-settings-flyout.component.scss']
})
export class GridSettingsFlyoutComponent implements AfterViewChecked, OnChanges {
  @Input() filters: ColumnFilter[] = [];
  @Input() quickFilters: QuickFilter[] = [];
  @Input() quickFilterValues: Record<string, string> = {};
  @Input() selectedSort: GridSortSettings | null = null;
  @Input() selectedGridView: GridView | null = null;
  @Input() gridViewColumns: DisplayGridColumnSettings[] = [];
  @Input() title: string = '';
  @Input() activeTab: string | null = null;
  @Input() preSelectedColumnProperty: string | null = null;
  @Input() getColumnDisplayName?: (column: any) => string;
  @Input() actionColumnProperty: string = 'action';
  @Input() canSaveView: boolean = true;
  @Input({ required: true }) gridId!: string;
  @Input() showFiltersTab: boolean = true;
  @Input() showSortTab: boolean = true;
  @Input() canFreezeColumn?: ReadonlySet<string>;
  @Input() extraTabs: GridSettingsExtraTab[] = [];
  @Output() close: EventEmitter<Lookup | null> = new EventEmitter<Lookup | null>();
  @Output() columnsChanged: EventEmitter<DisplayGridColumnSettings[]> = new EventEmitter<DisplayGridColumnSettings[]>();
  @Output() filtersChanged = new EventEmitter<{ filters: ColumnFilter[], changedProperty?: string }>();
  @Output() quickFilterToggled = new EventEmitter<QuickFilterToggle>();
  @Output() sortChanged = new EventEmitter<GridSortSettings | null>();
  @Output() frozenColumnsChanged = new EventEmitter<number>();

  readonly cannotSaveViewTooltip = 'Save the dashboard first to enable view saving';

  get canManage(): boolean {
    return this.selectedGridView?.canManage !== false;
  }

  isLoading: boolean = false;
  editingTitle: boolean = false;
  organizationLevels: Lookup[] = [];
  organizationLevelsHierarchical: OrganizationLevel[] = [];
  selectedOrgLevelId?: string | null = null;
  shareView: boolean = false;
  frozenColumnCount: number = 0;
  hasActiveQuickFilters = false;
  private frozenUids: Set<string> = new Set();
  @ViewChild('titleInput') titleInput!: ElementRef<HTMLInputElement>;
  @ViewChild('filterMenuTrigger') filterMenuTrigger!: MatMenuTrigger;
  @ViewChild('filterSearchInput') filterSearchInput!: SearchInputComponent;
  @ViewChild('sortSearchInput') sortSearchInput!: SearchInputComponent;
  filterMenuOpen = false;
  filterSearchQuery: string = '';
  filteredFilterColumns: DisplayGridColumnSettings[] = [];
  separatorKeysCodes: number[] = [ENTER, COMMA];
  searchQuery: string = '';
  filteredColumns: DisplayGridColumnSettings[] = [];
  sortMenuOpen = false;
  sortSearchQuery: string = '';
  filteredSortColumns: DisplayGridColumnSettings[] = [];
  private lastEmittedSort: GridSortSettings | null = null;
  private filterSearchTimeout: ReturnType<typeof setTimeout> | null = null;
  private sortSearchTimeout: ReturnType<typeof setTimeout> | null = null;
  private readonly SEARCH_DEBOUNCE_MS = 200;

  // Precomputed display names and filter type flags
  displayNameByProperty = new Map<string, string>();
  filterTypeFlagsByProperty = new Map<string, {
    isNumber: boolean;
    isDate: boolean;
    isEnum: boolean;
    isObject: boolean;
    isBoolean: boolean;
    isString: boolean;
  }>();

  constructor(
    private gridViewService: GridViewService,
    private toastService: ToastService,
    private orgLevelShareService: OrganizationLevelShareService,
    public gridSettingsService: GridSettingsService
  ) { }

  private get visibleConfigurableColumns(): DisplayGridColumnSettings[] {
    return this.gridViewColumns?.filter(c => c.visible && c.isDisplayNameConfigurable) ?? [];
  }

  get visibleColumnCount(): number {
    return this.visibleConfigurableColumns.length;
  }

  get activeFiltersCount(): number {
    if (!this.selectedGridView) return this.filters.length + Object.keys(this.quickFilterValues).length;

    const filters = this.selectedGridView.settingsJson?.filters ?? {};
    const uniqueFields = new Set<string>();

    Object.entries(filters).forEach(([key, value]) => {
      let fieldName: string;
      try {
        const parsed = JSON.parse(value as string);
        if (parsed && typeof parsed === 'object' && parsed.Field) {
          fieldName = parsed.Field;
        } else {
          fieldName = key;
        }
      } catch {
        fieldName = key;
      }
      uniqueFields.add(fieldName);
    });

    return uniqueFields.size + this.filters.filter(f => f.inherited).length;
  }

  ngOnInit(): void {
    this.filteredColumns = [...this.gridViewColumns];
    this.loadOrganizationLevels();
    this.shareView = this.selectedGridView?.isShared || false;
    this.selectedOrgLevelId = this.selectedGridView?.organizationLevelId || null;
    if (this.selectedGridView) {
      const inheritedFilters = this.filters.filter(f => f.inherited);
      const savedFilters = this.gridSettingsService.loadFiltersFromSettings(this.selectedGridView, this.gridViewColumns);
      this.filters = [...savedFilters, ...inheritedFilters];
    }
    this.gridSettingsService.populateObjectOptions(this.gridViewColumns, this.filters);
    this.selectedSort = this.selectedGridView?.settingsJson?.sort ? { ...this.selectedGridView.settingsJson.sort } : null;
    this.filteredSortColumns = this.gridViewColumns.filter(col => !!col.type && !col.disabled && col.property !== 'action');
    this.loadFrozenState();
    this.rebuildDisplayNames();
    this.rebuildFilterTypeFlags();
  }

  private rebuildDisplayNames(): void {
    const map = new Map<string, string>();
    this.gridViewColumns.forEach(col => {
      map.set(col.property, this.getDisplayName(col));
    });
    this.displayNameByProperty = map;
  }

  private rebuildFilterTypeFlags(): void {
    const map = new Map<string, {
      isNumber: boolean;
      isDate: boolean;
      isEnum: boolean;
      isObject: boolean;
      isBoolean: boolean;
      isString: boolean;
    }>();
    this.filters.forEach(filter => {
      map.set(filter.column.property, {
        isNumber: this.gridSettingsService.isNumberFilter(filter),
        isDate: this.gridSettingsService.isDateFilter(filter),
        isEnum: this.gridSettingsService.isEnumFilter(filter),
        isObject: this.gridSettingsService.isObjectFilter(filter),
        isBoolean: this.gridSettingsService.isBooleanFilter(filter),
        isString: this.gridSettingsService.isStringFilter(filter)
      });
    });
    this.filterTypeFlagsByProperty = map;
  }

  ngAfterViewChecked() {
    if (this.editingTitle && this.titleInput) {
      this.titleInput.nativeElement.focus();
    }
  }

  getDisplayName(column: DisplayGridColumnSettings): string {
    if (this.getColumnDisplayName) {
      return this.getColumnDisplayName(column);
    }
    return column.displayName;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedSort'] && changes['selectedSort'].currentValue) {
      const newSort = changes['selectedSort'].currentValue as GridSortSettings;
      if (!this.sortEquals(this.selectedSort, newSort)) {
        this.selectedSort = { ...newSort };
      }
    }
    if (changes['filters'] && changes['filters'].currentValue) {
      const newFilters = changes['filters'].currentValue as ColumnFilter[];
      if (this.filtersKey(this.filters) !== this.filtersKey(newFilters)) {
        this.filters = [...newFilters];
      }
      this.rebuildFilterTypeFlags();
    }
    if (changes['quickFilters'] || changes['quickFilterValues']) {
      this.hasActiveQuickFilters = this.quickFilters.some(quickFilter => this.quickFilterValues[quickFilter.key] !== undefined);
    }
    if (changes['gridViewColumns'] && changes['gridViewColumns'].currentValue) {
      this.rebuildDisplayNames();
    }
    if (changes['preSelectedColumnProperty'] && changes['preSelectedColumnProperty'].currentValue) {
      const columnProperty = changes['preSelectedColumnProperty'].currentValue as string;
      setTimeout(() => {
        this.addFilterForColumn(columnProperty);
      }, 0);
    }
  }

  private filtersKey(filters: ColumnFilter[]): string {
    return JSON.stringify((filters ?? []).map(f => ({
      property: f.column?.property,
      operator: f.operator,
      value: f.value,
      valueArray: f.valueArray,
      inherited: f.inherited,
    })));
  }

  private sortEquals(a: GridSortSettings | null, b: GridSortSettings | null): boolean {
    return a?.sortBy === b?.sortBy && a?.sortOrder === b?.sortOrder;
  }

  loadOrganizationLevels(): void {
    this.isLoading = true;
    this.orgLevelShareService.loadOrganizationLevels().subscribe({
      next: (data) => {
        this.organizationLevels = data.flat;
        this.organizationLevelsHierarchical = data.hierarchical;
        this.isLoading = false;
      },
      error: (err) => {
        this.isLoading = false;
      }
    });
  }

  isStringFilter(filter: ColumnFilter): boolean {
    return this.gridSettingsService.isStringFilter(filter);
  }

  isEnumFilter(filter: ColumnFilter): boolean {
    return this.gridSettingsService.isEnumFilter(filter);
  }

  isObjectFilter(filter: ColumnFilter): boolean {
    return this.gridSettingsService.isObjectFilter(filter);
  }

  isBooleanFilter(filter: ColumnFilter): boolean {
    return this.gridSettingsService.isBooleanFilter(filter);
  }

  isDateFilter(filter: ColumnFilter): boolean {
    return this.gridSettingsService.isDateFilter(filter);
  }

  isNumberFilter(filter: ColumnFilter): boolean {
    return this.gridSettingsService.isNumberFilter(filter);
  }

  getEnumDisplayName(enumMap: { value: number, name: string }[] | undefined, value: number): string {
    return this.gridSettingsService.getEnumDisplayName(enumMap, value);
  }

  getObjectDisplayName(property: string, id: number): string {
    return this.gridSettingsService.getObjectDisplayName(property, id, this.gridSettingsService.objectOptions);
  }

  getDisplayOperator(filter: ColumnFilter): string {
    if (this.isDateFilter(filter)) {
      const dateOp = this.gridSettingsService.dateOperators.find(o => o.value === filter.operator);
      if (dateOp) return dateOp.label;
    }
    if (this.isNumberFilter(filter)) {
      const numOp = this.gridSettingsService.numberOperators.find(o => o.value === filter.operator);
      if (numOp) return numOp.label;
    }
    if (this.isStringFilter(filter)) {
      const stringOp = this.gridSettingsService.stringOperators.find(o => o.value === filter.operator);
      if (stringOp) return stringOp.label;
    }
    if (filter.column.enumMap || this.hasValidObjectMap(filter.column.objectMap) || filter.column.type === 'boolean') return 'is';
    return String(filter.operator);
  }

  onSearch(): void {
    const query = this.searchQuery.trim().toLowerCase();
    this.filteredColumns = query
      ? this.gridViewColumns.filter(col => col.displayName.toLowerCase().includes(query))
      : [...this.gridViewColumns];
  }

  onFilterSearchInput(value: string): void {
    this.handleSearchInput(value, 'filter');
  }

  onFilterSearch(): void {
    const query = this.filterSearchQuery.trim().toLowerCase();
    this.filteredFilterColumns = this.gridViewColumns
      .filter(col =>
        col.displayName.toLowerCase().includes(query) &&
        col.property !== 'action' &&
        !this.filters.some(f => f.column.property === col.property)
      );
    this.restoreFocus(this.filterSearchInput);
  }

  openAddFilterMenu(): void {
    this.filterMenuOpen = true;
    this.filterSearchQuery = '';
    this.filteredFilterColumns = this.getAvailableFilterColumns();
  }

  selectFilterColumn(column: DisplayGridColumnSettings): void {
    this.disableAllFilters();
    this.filters.push({
      column,
      operator: FilterOperator.Equal,
      value: '',
      valueArray: [],
      isEditing: true,
      search: ''
    });
    this.filterSearchQuery = '';
    this.filteredFilterColumns = [];
    this.filterMenuTrigger.closeMenu();
    this.rebuildFilterTypeFlags();
  }

  addFilterForColumn(columnProperty: string): void {
    const column = this.gridViewColumns.find(col => col.property === columnProperty);
    if (!column) return;

    const existingFilterIndex = this.filters.findIndex(f => f.column.property === columnProperty);
    if (existingFilterIndex >= 0) {
      this.editFilter(existingFilterIndex);
    } else {
      this.selectFilterColumn(column);
    }
  }

  disableAllFilters(): void {
    this.filters.forEach(f => f.isEditing = false);
  }

  editFilter(index: number): void {
    this.filters.forEach((f, i) => f.isEditing = i === index);
  }

  isInheritedFilter(filter: ColumnFilter): boolean {
    return filter.inherited === true;
  }

  removeFilter(index: number): void {
    const removed = this.filters[index];
    this.filters.splice(index, 1);
    this.rebuildFilterTypeFlags();
    this.filterChange(removed?.column?.property);
  }

  resetFilters(): void {
    this.filters = [];
    this.filterTypeFlagsByProperty = new Map();
    this.filterChange();
  }

  onOperatorChange(filter: ColumnFilter) {
    if (filter.operator !== FilterOperator.Contains) {
      filter.value = '';
      filter.valueArray = [];
    }
    this.filterChange(filter.column.property);
  }

  filterChange(changedProperty?: string): void {
    if (this.selectedGridView?.settingsJson) {
      this.selectedGridView.settingsJson.filters = this.gridSettingsService.buildViewFilters(this.filters, this.quickFilterValues);
    }
    if (changedProperty) {
      const filter = this.filters.find(f => f.column.property === changedProperty);
      if (filter && filter.column.type === 'number'
        && filter.operator !== FilterOperator.IsEmpty
        && filter.operator !== FilterOperator.IsNotEmpty) {
        const hasValue = filter.operator === FilterOperator.Between
          ? filter.valueArray?.length === 2
          : filter.value != null && filter.value !== '';
        if (!hasValue) return;
      }
    }
    this.filtersChanged.emit({ filters: this.filters, changedProperty });
  }

  getFilteredOptions(filter: ColumnFilter, isEnum: boolean = false) {
    return this.gridSettingsService.getFilteredOptions(filter, this.gridSettingsService.objectOptions, isEnum);
  }

  hasValidObjectMap(obj: any): boolean {
    return this.gridSettingsService.hasValidObjectMap(obj);
  }

  onDrop(event: CdkDragDrop<DisplayGridColumnSettings[]>): void {
    if (this.searchQuery) return;
    moveItemInArray(this.gridViewColumns, event.previousIndex, event.currentIndex);
    this.gridViewColumns.forEach((col, index) => (col.order = index));
    this.onSearch();
    this.columnsChanged.emit(this.gridViewColumns);

    if (this.frozenUids.size > 0) {
      const visible = this.visibleConfigurableColumns;
      let newFreezeCount = 0;
      for (let i = 0; i < visible.length; i++) {
        if (this.frozenUids.has(visible[i]._uid)) {
          newFreezeCount++;
        } else {
          break;
        }
      }
      this.setFrozenState(visible.slice(0, newFreezeCount));
    }
  }

  areAllVisible(): boolean {
    return this.gridViewColumns.every(col => col.visible);
  }

  toggleAll(visible: boolean): void {
    this.gridViewColumns.forEach(col => (col.visible = visible));
    if (!visible && this.frozenUids.size > 0) {
      this.frozenUids.clear();
      this.frozenColumnCount = 0;
      this.frozenColumnsChanged.emit(0);
    }
    this.onSearch();
    this.columnsChanged.emit(this.gridViewColumns);
  }

  toggleColumnVisibility(column: DisplayGridColumnSettings): void {
    if (!column.visible && this.frozenUids.has(column._uid)) {
      this.frozenUids.delete(column._uid);
      this.frozenColumnCount = this.frozenUids.size;
      this.frozenColumnsChanged.emit(this.frozenColumnCount);
    } else if (column.visible && this.frozenUids.size > 0) {
      const visible = this.visibleConfigurableColumns;
      const lastFrozenVisibleIdx = this.findLastFrozenVisibleIndex(visible);

      if (lastFrozenVisibleIdx >= 0) {
        const colVisibleIndex = visible.findIndex(c => c._uid === column._uid);
        if (colVisibleIndex >= 0 && colVisibleIndex < lastFrozenVisibleIdx) {
          const lastFrozenCol = visible[lastFrozenVisibleIdx];
          const currentFullIndex = this.gridViewColumns.findIndex(c => c._uid === column._uid);
          const lastFrozenFullIndex = this.gridViewColumns.findIndex(c => c._uid === lastFrozenCol._uid);
          if (currentFullIndex >= 0 && lastFrozenFullIndex >= 0 && currentFullIndex < lastFrozenFullIndex) {
            const [removed] = this.gridViewColumns.splice(currentFullIndex, 1);
            this.gridViewColumns.splice(lastFrozenFullIndex, 0, removed);
            this.gridViewColumns.forEach((col, idx) => { col.order = idx; });
            this.onSearch();
          }
        }
      }
    }
    this.columnsChanged.emit(this.gridViewColumns);
  }

  onShareToggle(checked: boolean): void {
    const result = this.orgLevelShareService.handleShareToggle(checked, this.selectedOrgLevelId);
    if (!checked) this.selectedOrgLevelId = result.orgLevelId;
  }

  toggleShareView(): void {
    if (!this.canManage) return;
    this.shareView = !this.shareView;
    this.onShareToggle(this.shareView);
  }

  openAddSortMenu(): void {
    this.sortMenuOpen = true;
    this.sortSearchQuery = '';
    this.filteredSortColumns = this.getAvailableSortColumns();
  }

  onFilterMenuClosed(): void {
    this.filterMenuOpen = false;
  }

  private getAvailableFilterColumns(): DisplayGridColumnSettings[] {
    return this.gridViewColumns.filter(col =>
      !!col.type &&
      col.isFilterable &&
      !col.hiddenFromFilter &&
      !col.disabled &&
      col.property !== 'action' &&
      !this.filters.some(f => f.column.property === col.property)
    );
  }

  private getAvailableSortColumns(): DisplayGridColumnSettings[] {
    return this.gridViewColumns.filter(col =>
      !!col.type &&
      col.isSortable &&
      !col.disabled &&
      col.property !== 'action' &&
      (!this.selectedSort || this.selectedSort.sortBy !== col.property)
    );
  }

  onSave(): void {
    this.updateGridViewSettings();
    this.isLoading = true;

    const isUpdate = !!this.selectedGridView?.id;

    saveGridView(this.gridViewService, this.selectedGridView!).subscribe({
      next: (view: GridView | undefined) => {
        if (!isUpdate && view) this.selectedGridView = view;
        this.handleSaveSuccess(isUpdate);
      },
      error: (error) => this.handleSaveError(isUpdate, error)
    });
  }

  private updateGridViewSettings(): void {
    if (this.showFiltersTab) {
      this.selectedGridView!.settingsJson!.filters = this.gridSettingsService.buildViewFilters(this.filters, this.quickFilterValues);
    }
    this.selectedGridView!.settingsJson!.columns = this.gridViewColumns
      .filter(col => col.property !== 'action')
      .map(col => ({
        property: col.property,
        visible: col.visible,
        order: col.order
      }));
    if (this.showSortTab) {
      this.selectedGridView!.settingsJson!.sort = this.selectedSort ? { ...this.selectedSort } : undefined;
    }
    this.selectedGridView!.settingsJson!.frozenColumnCount = this.frozenColumnCount;
    // gridId is consumed only by OnBeforeCreate on the BE (UserViews registration); no-op on updates
    this.selectedGridView!.gridId = this.gridId;
    this.selectedGridView!.isShared = this.shareView;

    if (this.shareView && this.selectedOrgLevelId) {
      this.selectedGridView!.organizationLevelId = this.selectedOrgLevelId;
      this.selectedGridView!.isShared = true;
    }
  }

  private handleSaveSuccess(isUpdate: boolean): void {
    this.isLoading = false;
    const message = isUpdate ? 'Grid view updated successfully' : 'Grid view created successfully';
    this.toastService.showSuccess(message, true);
    this.close.emit({ id: this.selectedGridView!.id, name: this.selectedGridView!.viewName });
  }

  private handleSaveError(isUpdate: boolean, error: HttpErrorResponse): void {
    this.isLoading = false;
    const message = isUpdate ? 'Error updating grid view' : 'Error creating grid view';
    this.toastService.showError(message);
  }

  onClose(): void {
    this.selectedOrgLevelId = null;
    this.shareView = false;
    this.close.emit(null);
  }

  saveTitle(): void {
    this.editingTitle = false;
    if (this.selectedGridView) this.selectedGridView.viewName = this.title;
  }

  addChip(event: MatChipInputEvent, filter: ColumnFilter): void {
    const value = (event.value || '').trim();
    if (value) {
      filter.valueArray = filter.valueArray || [];
      filter.valueArray.push(value);
      this.updateFilterValue(filter);
    }
    event.chipInput!.clear();
  }

  removeChip(filter: ColumnFilter, index: number): void {
    if (filter.valueArray && index >= 0) {
      filter.valueArray.splice(index, 1);
      this.updateFilterValue(filter);
    }
  }

  editChip(filter: ColumnFilter, chipIdx: number, event: MatChipEditedEvent): void {
    const value = event.value.trim();
    if (!value) {
      this.removeChip(filter, chipIdx);
      return;
    }
    filter.valueArray[chipIdx] = value;
    this.updateFilterValue(filter);
  }

  private updateFilterValue(filter: ColumnFilter): void {
    filter.value = filter.valueArray.join(',');
    this.filterChange(filter.column.property);
  }

  onSortSearchInput(value: string): void {
    this.handleSearchInput(value, 'sort');
  }

  onSortSearch(): void {
    const query = this.sortSearchQuery.trim().toLowerCase();
    this.filteredSortColumns = this.gridViewColumns.filter(col =>
      col.displayName.toLowerCase().includes(query) &&
      col.property !== 'action' &&
      !col.disabled &&
      (!this.selectedSort || this.selectedSort.sortBy !== col.property)
    );
    this.restoreFocus(this.sortSearchInput);
  }

  private handleSearchInput(value: string, type: 'filter' | 'sort'): void {
    if (type === 'filter') {
      this.filterSearchQuery = value;
      if (this.filterSearchTimeout) clearTimeout(this.filterSearchTimeout);
      this.filterSearchTimeout = setTimeout(() => this.onFilterSearch(), this.SEARCH_DEBOUNCE_MS);
    } else {
      this.sortSearchQuery = value;
      if (this.sortSearchTimeout) clearTimeout(this.sortSearchTimeout);
      this.sortSearchTimeout = setTimeout(() => this.onSortSearch(), this.SEARCH_DEBOUNCE_MS);
    }
  }

  private restoreFocus(inputComponent: SearchInputComponent): void {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (inputComponent) {
          inputComponent.focus();
        }
      });
    });
  }

  selectSortColumn(column: DisplayGridColumnSettings): void {
    this.selectedSort = {
      sortBy: column.property,
      sortOrder: 'asc'
    };
    this.sortSearchQuery = '';
    this.filteredSortColumns = [];
    this.sortMenuOpen = false;
    this.sortChange();
  }

  onSortMenuClosed(): void {
    this.sortMenuOpen = false;
  }

  onSortDirectionChange(): void {
    this.sortChange();
  }

  deleteSort(): void {
    this.selectedSort = null;
    this.sortChange(true);
  }

  sortChange(force: boolean = false): void {
    if (this.selectedGridView?.settingsJson) {
      this.selectedGridView.settingsJson.sort = this.selectedSort ? { ...this.selectedSort } : undefined;
    }

    if (force || !this.sortEquals(this.lastEmittedSort, this.selectedSort)) {
      this.lastEmittedSort = this.selectedSort ? { ...this.selectedSort } : null;
      this.sortChanged.emit(this.selectedSort);
    }
  }

  getSortColumnDisplayName(property: string): string {
    const col = this.gridViewColumns.find(c => c.property === property);
    return col ? col.displayName : property;
  }

  trackByProperty(index: number, column: DisplayGridColumnSettings): string {
    return column.property;
  }

  public trackByIndex = trackByIndex;
  public trackById = trackById;

  isColumnFrozen(column: DisplayGridColumnSettings): boolean {
    return this.frozenUids.has(column._uid);
  }

  getFreezeTooltipKey(column: DisplayGridColumnSettings): string {
    return this.isColumnFrozen(column) ? 'Unfreeze columns up to here' : 'Freeze columns up to here';
  }

  toggleFreezeAtColumn(column: DisplayGridColumnSettings): void {
    const visible = this.visibleConfigurableColumns;
    const clickedIndex = visible.findIndex(c => c._uid === column._uid);
    if (clickedIndex < 0) return;

    const newFreezeCount = clickedIndex + 1;

    if (newFreezeCount >= visible.length) {
      this.toastService.displayErrorMessage('Cannot freeze all columns', true);
      return;
    }

    if (this.frozenColumnCount === newFreezeCount) {
      this.setFrozenState([]);
    } else {
      this.setFrozenState(visible.slice(0, newFreezeCount));
    }
  }

  private loadFrozenState(): void {
    const settings = this.selectedGridView?.settingsJson;
    const count = settings?.frozenColumnCount ?? 0;
    const visible = this.visibleConfigurableColumns;
    const capped = Math.min(count, Math.max(0, visible.length - 1));
    this.setFrozenState(visible.slice(0, capped), false);
  }

  private setFrozenState(columns: DisplayGridColumnSettings[], emit: boolean = true): void {
    this.frozenUids = new Set(columns.map(c => c._uid));
    this.frozenColumnCount = this.frozenUids.size;
    if (emit) {
      this.frozenColumnsChanged.emit(this.frozenColumnCount);
    }
  }

  private findLastFrozenVisibleIndex(visible: DisplayGridColumnSettings[]): number {
    let lastIdx = -1;
    for (let i = 0; i < visible.length; i++) {
      if (this.frozenUids.has(visible[i]._uid)) {
        lastIdx = i;
      }
    }
    return lastIdx;
  }
}
